import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Pencil, Trash2, Plus, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const COMMISSION_CATEGORIES = ['Equity', 'Debt', 'Hybrid', 'Index / ETF', 'Solution Oriented', 'Other'];
const EMPTY = { fund_house: '', scheme: '', category: 'Equity', trail_percent: '' };
const inputCls = 'w-full px-3 py-2 rounded-lg border border-[#E5E5E5] focus:ring-2 focus:ring-[#A0451E]/50 bg-white font-body text-sm';

const CommissionManager = () => {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/commissions`);
      setRows(res.data);
    } catch {
      toast.error('Could not load commission rates.');
    }
  };

  useEffect(() => {
    load();
  }, []);

  const reset = () => {
    setForm(EMPTY);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, trail_percent: Number(form.trail_percent) };
    try {
      if (editingId) await axios.put(`${API}/commissions/${editingId}`, payload);
      else await axios.post(`${API}/commissions`, payload);
      toast.success(editingId ? 'Rate updated' : 'Rate added');
      reset();
      load();
    } catch {
      toast.error('Could not save. Check that all fields are filled and trail % is between 0 and 10.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this commission rate?')) return;
    try {
      await axios.delete(`${API}/commissions/${id}`);
      toast.success('Rate deleted');
    } catch {
      toast.error('Could not delete this rate.');
    }
    load();
  };

  const edit = (r) => {
    setEditingId(r.commission_id);
    setForm({ fund_house: r.fund_house, scheme: r.scheme, category: r.category, trail_percent: r.trail_percent });
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6" data-testid="commission-manager">
      <form onSubmit={submit} className="bg-white rounded-xl border border-[#E5E5E5] p-6" data-testid="commission-form">
        <h3 className="text-lg font-heading font-medium text-[#A0451E] mb-4">{editingId ? 'Edit commission rate' : 'Add commission rate'}</h3>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <input required placeholder="Fund house (AMC)" value={form.fund_house} onChange={set('fund_house')} className={inputCls} data-testid="commission-fund-house-input" />
          <input required placeholder="Scheme name" value={form.scheme} onChange={set('scheme')} className={inputCls} data-testid="commission-scheme-input" />
          <select value={form.category} onChange={set('category')} className={inputCls} data-testid="commission-category-select">
            {COMMISSION_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <input required type="number" min="0" max="10" step="0.01" placeholder="Trail % p.a." value={form.trail_percent} onChange={set('trail_percent')} className={inputCls} data-testid="commission-trail-input" />
        </div>
        <div className="flex gap-3 mt-4">
          <button type="submit" disabled={saving} className="btn-primary inline-flex items-center gap-2" data-testid="commission-save-btn">
            <Plus className="w-4 h-4" /> {editingId ? 'Update' : 'Add'}
          </button>
          {editingId && (
            <button type="button" onClick={reset} className="inline-flex items-center gap-1 text-sm font-body text-[#737373]" data-testid="commission-cancel-btn">
              <X className="w-4 h-4" /> Cancel
            </button>
          )}
        </div>
      </form>

      <div className="bg-white rounded-xl border border-[#E5E5E5] overflow-x-auto">
        <table className="w-full text-sm font-body" data-testid="commission-admin-table">
          <thead className="bg-[#FDF8F5] text-left text-[#A0451E]">
            <tr><th className="p-3">Fund House</th><th className="p-3">Scheme</th><th className="p-3">Category</th><th className="p-3">Trail % p.a.</th><th className="p-3" /></tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={5} className="p-6 text-center text-[#737373]" data-testid="commission-admin-empty">No rates added yet.</td></tr>
            )}
            {rows.map((r) => (
              <tr key={r.commission_id} className="border-t border-[#E5E5E5]" data-testid={`commission-admin-row-${r.commission_id}`}>
                <td className="p-3">{r.fund_house}</td>
                <td className="p-3">{r.scheme}</td>
                <td className="p-3">{r.category}</td>
                <td className="p-3">{r.trail_percent.toFixed(2)}%</td>
                <td className="p-3 whitespace-nowrap text-right">
                  <button onClick={() => edit(r)} className="p-2 text-[#A0451E]" data-testid={`commission-edit-${r.commission_id}`}><Pencil className="w-4 h-4" /></button>
                  <button onClick={() => remove(r.commission_id)} className="p-2 text-red-600" data-testid={`commission-delete-${r.commission_id}`}><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CommissionManager;
